import React, { useEffect, useRef } from "react";
import { Stack, Typography } from "@mui/joy";
import FullPageContainer from "./FullPageContainer";
import { theme } from "styles/theme";
import BackButton from "components/common/BackButton";
import { useOnboardingProgress } from "hooks/useOnboardingProgress";
import useSafeAreaColor from "hooks/useSafeAreaColor";
import ProgressBarWithSection from "../../components/onboarding/ProgressBarWithSection";
import CloseButtonSmall from "../../components/task/change-status-button/CloseButtonSmall";

export default function FullPageWithHeaderAndProgress({
  headerTitle,
  hideHeader,
  hideProgress,
  backgroundColor = theme.vars.palette.Background.Level1,
  body,
  disableBack,
  hideBack = false,
  onBack,
  hideClose = false,
  paddingXContent = 20,
  onClose,
  disableSafeAreaColorSync = false,
}: {
  headerTitle?: string;
  hideHeader?: boolean;
  hideProgress?: boolean;
  backgroundColor?: string;
  body?: React.ReactNode;
  disableBack?: boolean;
  hideBack?: boolean;
  onBack?: () => void;
  hideClose?: boolean;
  paddingXContent?: number;
  onClose?: () => void;
  // 상위에서 safe-area 색을 단일 색으로 고정 관리하는 화면(promotion 퍼널)이
  // 컨테이너의 스텝별 동기화를 끌 때 사용. 페이지 배경 페인트는 그대로 유지.
  disableSafeAreaColorSync?: boolean;
}) {
  const headerHeight = 60; // 헤더 높이
  const progressBarHeight = 12; // 프로그레스바 높이
  const progressPaddingX = 20; // 프로그래스바 양쪽 패딩 값

  const paddingXHeader = 8;
  const paddingTopContent = hideProgress
    ? hideHeader
      ? headerHeight + 8
      : 8
    : hideHeader
      ? headerHeight + 32
      : 32;

  const paddingBottomContent = hideProgress ? 8 : 32;
  const containerRef = useRef<HTMLDivElement>(null);

  // 화면 잘림 문제
  useEffect(() => {
    // 페이지 전환(혹은 특정 이벤트) 시점에
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [body]);

  const { progress, sectionCount } = useOnboardingProgress();

  useSafeAreaColor(disableSafeAreaColorSync ? undefined : backgroundColor);

  return (
    <FullPageContainer backgroundColor={backgroundColor}>
      <Stack
        direction={"column"}
        justifyContent={"flex-start"}
        sx={{
          maxWidth: "660px",
          width: "100vw",
          mx: "auto",
          height: "100vh",
        }}
      >
        {!hideHeader && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              width: "100%",
              px: `${paddingXHeader}px`,
              position: "sticky",
              top: 0,
              zIndex: 100,
              height: `${headerHeight}px`, // 헤더 높이 설정
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {!hideBack && onBack && (
                <BackButton onBack={onBack} disabled={disableBack} />
              )}
              {headerTitle && (
                <Typography
                  variant="Body"
                  sx={{
                    px: 0,
                    fontColor: theme.vars.palette.Content.Strong,
                    fontWeight: 700,
                  }}
                >
                  {headerTitle}
                </Typography>
              )}
            </Stack>
            {!hideClose && onClose && <CloseButtonSmall />}
          </Stack>
        )}
        {!hideProgress && (
          <Stack
            sx={{
              width: "100%",
              px: `${progressPaddingX}px`,
              mt: 1,
              position: "sticky",
              top: `${headerHeight}px`, // 헤더 높이만큼 아래로 이동
              zIndex: 99,
              height: `${progressBarHeight}px`, // 프로그레스바 높이 설정
            }}
          >
            <ProgressBarWithSection
              progress={progress}
              sectionCount={sectionCount}
            />
          </Stack>
        )}
        <Stack
          ref={containerRef}
          sx={{
            width: "100%",
            flex: 1,
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            px: `${paddingXContent}px`,
            pt: `${paddingTopContent}px`,
            pb: `${paddingBottomContent}px`,
            position: "relative",
          }}
        >
          {body}
        </Stack>
      </Stack>
    </FullPageContainer>
  );
}
